'use client';

import { cn } from '@/lib/utils';
import { AURORA_CALENDAR } from '@/lib/aurora';
import SectionHeading from '@/components/ui/SectionHeading';
import {
  resolveAuroraLabels,
  type AuroraLabels,
} from '@/components/aurora/labels';

// AURORA_CALENDAR starts at Sep (index 0) = JS month 8
const monthMapping = [8, 9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7];

export default function AuroraCalendar({ labels }: { labels?: AuroraLabels }) {
  const L = resolveAuroraLabels(labels);
  const currentMonth = new Date().getMonth();

  return (
    <div>
      <SectionHeading
        title={L.calendarTitle}
        subtitle={L.calendarSubtitle}
        className="mb-8"
      />

      <div
        className={cn(
          'rounded-xl overflow-hidden',
          'bg-white shadow-sm',
        )}
      >
        <div className="overflow-x-auto">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr className="bg-[#F0EDE5]">
                {[
                  L.calendarHeaders.month,
                  L.calendarHeaders.darkHours,
                  L.calendarHeaders.season,
                  L.calendarHeaders.typicalKp,
                  L.calendarHeaders.notes,
                ].map(
                  (header) => (
                    <th
                      key={header}
                      className="px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider text-[#9B7B3A]"
                    >
                      {header}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {AURORA_CALENDAR.map((row, i) => {
                const isCurrentMonth = monthMapping[i] === currentMonth;
                const isEven = i % 2 === 0;
                const isSeason = row.season;

                return (
                  <tr
                    key={row.month}
                    className={cn(
                      'transition-colors',
                      !isSeason && 'opacity-50',
                      isCurrentMonth
                        ? 'bg-[#9B7B3A]/10 border-l-2 border-l-[#9B7B3A]'
                        : isEven
                          ? 'bg-white'
                          : 'bg-[#FAFAF5]',
                    )}
                  >
                    <td
                      className={cn(
                        'px-4 py-3 font-medium whitespace-nowrap',
                        isCurrentMonth
                          ? 'text-[#9B7B3A] font-bold'
                          : 'text-[#1A1A2E]',
                      )}
                    >
                      {L.calendarMonths[row.month] ?? row.month}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 whitespace-nowrap',
                        'text-[#1A1A2E]',
                      )}
                    >
                      {row.darkHours}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {isSeason ? (
                        <span className="text-[#22C55E] font-bold">{'\u2713'}</span>
                      ) : (
                        <span className="text-[#EF4444] font-bold">{'\u2717'}</span>
                      )}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 whitespace-nowrap',
                        'text-[#1A1A2E]',
                      )}
                    >
                      {row.typicalKp}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3',
                        'text-[#5A5A6E]',
                      )}
                    >
                      {L.calendarNotes[row.month] ?? row.notes}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
